import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  useGetAllCategories,
  useGetAllSubCategories,
  useGetAllSubSubCategories,
} from "@/api/queries";
import { Category, SubCategory, SubSubCategory } from "@/api/types";
import { ChevronRight, Grid3X3, Layers, Loader2 } from "lucide-react";
import placeholder from "@/assets/img/placeholder.jpg";

export const Categories = () => {
  const navigate = useNavigate();
  const { categoryId, subcategoryId } = useParams<{
    categoryId?: string;
    subcategoryId?: string;
  }>();

  // States for navigation levels
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategory | null>(null);
  const [viewLevel, setViewLevel] = useState<"categories" | "subcategories" | "subsubcategories">(
    "categories",
  );

  // Fetch categories
  const { data: categoriesData, isLoading: categoriesLoading } = useGetAllCategories();
  const categories = useMemo(() => categoriesData?.data || [], [categoriesData]);

  // Fetch subcategories when a category is selected
  const { data: subCategoriesData, isLoading: subCategoriesLoading } = useGetAllSubCategories(
    selectedCategory?.id || "",
  );
  const subCategories = useMemo(() => subCategoriesData?.data || [], [subCategoriesData]);

  // Fetch subsubcategories when a subcategory is selected
  const { data: subSubCategoriesData, isLoading: subSubCategoriesLoading } =
    useGetAllSubSubCategories(selectedSubCategory?.id || "");
  const subSubCategories = useMemo(() => subSubCategoriesData?.data || [], [subSubCategoriesData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryId, subcategoryId, viewLevel]);

  // Set initial state based on URL params
  useEffect(() => {
    if (categoryId) {
      const category = categories.find((cat) => cat.id === categoryId);
      if (category) {
        setSelectedCategory(category);
        setViewLevel("subcategories");

        if (subcategoryId) {
          const subcategory = subCategories.find((subcat) => subcat.id === subcategoryId);
          if (subcategory) {
            setSelectedSubCategory(subcategory);
            setViewLevel("subsubcategories");
          }
        }
      }
    }
  }, [categoryId, subcategoryId, categories, subCategories]);

  // Handle category selection
  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category);
    setSelectedSubCategory(null);
    setViewLevel("subcategories");
    navigate(`/categories/${category.id}`);
  };

  // Handle subcategory selection
  const handleSubCategorySelect = (subcategory: SubCategory) => {
    setSelectedSubCategory(subcategory);
    setViewLevel("subsubcategories");
    navigate(`/categories/${selectedCategory?.id}/${subcategory.id}`);
  };

  // Handle subsubcategory selection - navigate to products
  const handleSubSubCategorySelect = (subsubcategory: SubSubCategory) => {
    navigate(`/shop?subsubcategory=${subsubcategory.id}`);
  };

  // Navigate back to categories
  const navigateToCategories = () => {
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setViewLevel("categories");
    navigate("/categories");
  };

  // Navigate back to subcategories
  const navigateToSubCategories = () => {
    setSelectedSubCategory(null);
    setViewLevel("subcategories");
    navigate(`/categories/${selectedCategory?.id}`);
  };

  // Loading state
  if (
    (categoriesLoading && viewLevel === "categories") ||
    (subCategoriesLoading && viewLevel === "subcategories") ||
    (subSubCategoriesLoading && viewLevel === "subsubcategories")
  ) {
    return (
      <div className='flex items-center justify-center min-h-[60vh]'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
        <span className='ml-2'>Loading...</span>
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-12'>
      {/* Breadcrumb Navigation */}
      <div className='flex items-center mb-8 text-sm'>
        <Button
          variant='ghost'
          className='p-0 h-auto font-medium hover:bg-transparent hover:underline'
          onClick={navigateToCategories}
        >
          Categories
        </Button>

        {selectedCategory && (
          <>
            <ChevronRight className='h-4 w-4 mx-2 text-gray-400' />
            <Button
              variant='ghost'
              className={`p-0 h-auto font-medium hover:bg-transparent hover:underline ${
                viewLevel === "subcategories" ? "text-primary" : ""
              }`}
              onClick={navigateToSubCategories}
            >
              {selectedCategory.name}
            </Button>
          </>
        )}

        {selectedSubCategory && (
          <>
            <ChevronRight className='h-4 w-4 mx-2 text-gray-400' />
            <span className='text-primary font-medium'>{selectedSubCategory.name}</span>
          </>
        )}
      </div>

      {/* Page Title */}
      <div className='mb-8'>
        <h1 className='text-3xl font-bold'>
          {viewLevel === "categories"
            ? "Browse Categories"
            : viewLevel === "subcategories"
            ? `${selectedCategory?.name} Categories`
            : `${selectedSubCategory?.name} Categories`}
        </h1>
        <p className='text-gray-600 mt-2'>
          {viewLevel === "categories"
            ? "Explore our wide range of product categories"
            : viewLevel === "subcategories"
            ? `Browse subcategories in ${selectedCategory?.name}`
            : `Browse detailed categories in ${selectedSubCategory?.name}`}
        </p>
      </div>

      {/* Categories Grid */}
      {viewLevel === "categories" && (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {categories.map((category) => (
            <Card
              key={category.id}
              className='overflow-hidden hover:shadow-md transition-shadow cursor-pointer'
              onClick={() => handleCategorySelect(category)}
            >
              <CardHeader className='pb-2'>
                <CardTitle className='flex items-center'>
                  <Grid3X3 className='h-5 w-5 mr-2 text-primary' />
                  {category.name}
                </CardTitle>
                {category.image ? (
                  <div className='relative w-full h-32 mb-4 overflow-hidden rounded-lg bg-gray-100'>
                    <img
                      src={category.image}
                      alt={category.name}
                      className='w-full h-full object-cover transition-transform duration-300 hover:scale-105'
                      loading='lazy'
                    />
                    <div className='absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300' />
                  </div>
                ) : (
                  <div className='w-10 h-10 bg-gray-200 rounded flex items-center justify-center'>
                    <img
                      src={placeholder}
                      alt='placeholder'
                      className='w-10 h-10 object-cover rounded'
                    />
                  </div>
                )}

                <CardDescription>{category.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className='flex justify-between items-center'>
                  <span className='text-sm text-gray-500'>{category.count} products</span>
                  <Button variant='ghost' size='sm' className='text-primary'>
                    View <ChevronRight className='h-4 w-4 ml-1' />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Subcategories Grid */}
      {viewLevel === "subcategories" && (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {subCategories.length > 0 ? (
            subCategories.map((subcategory) => (
              <Card
                key={subcategory.id}
                className='overflow-hidden hover:shadow-md transition-shadow cursor-pointer'
                onClick={() => handleSubCategorySelect(subcategory)}
              >
                <CardHeader className='pb-2'>
                  <CardTitle className='flex items-center'>
                    <Layers className='h-5 w-5 mr-2 text-primary' />
                    {subcategory.name}
                  </CardTitle>
                  <CardDescription>{subcategory.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-gray-500'>{subcategory.count} products</span>
                    <Button variant='ghost' size='sm' className='text-primary'>
                      View <ChevronRight className='h-4 w-4 ml-1' />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className='col-span-full text-center py-12'>
              <p className='text-gray-500 mb-4'>
                No subcategories found for {selectedCategory?.name}
              </p>
              <Button onClick={navigateToCategories}>Back to Categories</Button>
            </div>
          )}
        </div>
      )}

      {/* SubSubcategories Grid */}
      {viewLevel === "subsubcategories" && (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {subSubCategories.length > 0 ? (
            subSubCategories.map((subsubcategory: SubSubCategory) => (
              <Card
                key={subsubcategory.id}
                className='overflow-hidden hover:shadow-md transition-shadow cursor-pointer'
                onClick={() => handleSubSubCategorySelect(subsubcategory)}
              >
                <CardHeader className='pb-2'>
                  <CardTitle>{subsubcategory.name}</CardTitle>
                  <CardDescription>{subsubcategory.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-gray-500'>{subsubcategory.count} products</span>
                    <Button variant='ghost' size='sm' className='text-primary'>
                      Browse Products <ChevronRight className='h-4 w-4 ml-1' />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className='col-span-full text-center py-12'>
              <p className='text-gray-500 mb-4'>
                No detailed categories found for {selectedSubCategory?.name}
              </p>
              <Button onClick={navigateToSubCategories}>
                Back to {selectedCategory?.name} Categories
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
